import { useEffect, useState } from 'react';
import type { MouseEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import type { CategoryPhTree } from '../../../../../../../../../../interfaces';

/**
 * Coordinates desktop category selection and deferred route changes.
 *
 * ---
 * 🧠 **Context**
 *
 * - Closes the menu before committing a selected route after 250 ms.
 * - Cancels the pending timer when the effect restarts or unmounts.
 *
 * ---
 * @example
 * ```ts
 * const categories = useCategoryNavigation();
 * const showMedicines = () => {
 *   categories.setSelectedDepartment({ id: 'medicamentos', name: 'Medicamentos' });
 *   categories.setOpen(true);
 * };
 * ```
 *
 * ---
 * @function useCategoryNavigation
 * @returns {ReturnType<typeof useCategoryNavigation>} Open state, department selection and deferred navigation handler.
 */
export function useCategoryNavigation() {
	const [open, setOpen] = useState(false);
	const [selectedDepartment, setSelectedDepartment] = useState<CategoryPhTree | undefined>();
	const [pendingNavigation, setPendingNavigation] = useState<string | null>(null);

	const navigate = useNavigate();

	const handleNavigateTo = (e: MouseEvent, link: string) => {
		e.preventDefault();
		setPendingNavigation(link);
		setOpen(false);
	};

	useEffect(() => {
		if (!open && pendingNavigation) {
			const timer = setTimeout(() => {
				navigate(pendingNavigation);
				setPendingNavigation(null);
			}, 250);

			return () => clearTimeout(timer);
		}
	}, [open, pendingNavigation]);

	return { open, setOpen, selectedDepartment, setSelectedDepartment, handleNavigateTo };
}
